import { Component, ViewEncapsulation, ViewChild, OnInit } from '@angular/core';
import { User } from '@task-modules/users';
import { TaskRoutes } from '@task-utils/routes';
import { Task } from '@task-utils/types';
import { Frame, language, color, modal, DataTableComponent, GenericFilterComponent, GenericFilter, template, notification, ConfirmDialogComponent } from 'curvy';
import { CreateEditLabelFlagsDialog } from './create-edit-label-flags.dialog';
import { label_flags_translations } from './label-flags.trans';



type TableType = DataTableComponent<Task.LabelFlags, DisplayLabelFlags, 'label-flags', GenericFilterComponent>;

@Component({
    selector: 'task-label-flags',
    template: `
        <div class="temp-add-button" *ngIf="can_add_new">
            <button (click)="add_order_flag()"
                class="filled pill"
                color="primary"
            >
                <i icon="add_circle"></i>
                <span>{{ 'LABEL.FLAG.ADD_NEW' | trans }}</span>
            </button>
        </div>
        <knw-data-table #labelFlagsTable [config]="tableConfig"></knw-data-table>
    `,
    styles: [`
        task-label-flags {
            display: flex;
            flex-flow: column nowrap;
            overflow: hidden !important;
        }
    `],
    encapsulation: ViewEncapsulation.None,

})

export class LabelFlagsComponent implements OnInit {
    @ViewChild('labelFlagsTable')
    table: TableType;

    can_add_new = false;

    constructor() {
        Frame.set({
            title: "NAVROUTE.LABEL.FLAGS",
            visible: true,
            layout: "middle",
            size: "full"
        });
        language.load_translations(label_flags_translations);
    }

    add_order_flag() {
        modal.open(CreateEditLabelFlagsDialog, (res) => {
            if (res) {
                this.table.getData();
            }
        })
    }

    ngOnInit(): void {
        this.init()
    }

    async init() {
        this.can_add_new = await User.currentTypeIs(
            Task.USER_TYPE.ADMIN,
            Task.USER_TYPE.SYSTEM_ADMIN,
            Task.USER_TYPE.SUPPLIER_ADMIN,
            Task.USER_TYPE.SUPER_USER
        )
    }

    async search_companies(search: string) {
        let res = await TaskRoutes.companies.api_company_search(
            { search: [search, Task.COMPANY_TYPE.MAIN], attributes: ["*I:+name+ +description+", "#=:company_type_id"] },
            { page_no: 1, page_size: 10 }
        );

        return res.data;
    }

    format_company_name(comp: Task.Company) {
        if (!comp) return "";
        return comp.name;
    }

    tableConfig: TableType["tableConfig"] = {
        tableName: 'order-flags',
        getData: async (filter, paginator, sort_header) => {
            let parsed_filter = { search: [], attributes: [], sort: null };

            for (let key in filter) {
                if (key === 'company' && filter['company'] !== null) {
                    parsed_filter.attributes.push('#=:company_id')
                    parsed_filter.search.push(filter[key].company_id)
                }
                else {
                    parsed_filter.attributes.push(key);
                    parsed_filter.search.push(filter[key]);
                }
            }

            if (sort_header) {
                parsed_filter.sort = sort_header;
            }

            return TaskRoutes.label_flags.api_label_flags_search(parsed_filter, paginator)
        },

        getHeader: (header) => {
            switch (header) {
                case 'short_name':
                    return { icon: 'short_text', label: 'LABEL.FLAG.SHORT.NAME', sortable: true }

                case 'name':
                    return { icon: 'text_fields', label: 'LABEL.FLAG.LONG.NAME', sortable: true }

                case 'flags':
                    return { icon: 'flag', label: 'LABEL.FLAG.APPLY', sortable: false }

                case 'supplier_visible':
                    return { icon: 'local_shipping', label: 'LABEL.FLAG.SUPPLIERS', sortable: false }
            }
        },

        unwrapData: (label_flag) => {
            let flags = "";
            let flags_obj: Record<string, string> = {};

            flags += `<div class="flex-row align-center" style="display: flex; align-items: center; justify-content: flex-start; gap: 2px">
                {{ticket_check | icon}}
                <span><strong>${language.translate('LABEL.FLAG.TICKET')}</strong></span>
            </div>`;
            flags_obj['ticket_check'] = label_flag.tickets ? 'check' : 'remove';

            flags += `<div class="flex-row align-center" style="display: flex; align-items: center; justify-content: flex-start; gap: 4px">
                {{orders_check | icon}}
                <span><strong>${language.translate('LABEL.FLAG.ORDER')}</strong></span>
            </div>`;
            flags_obj['orders_check'] = label_flag.orders ? 'check' : 'remove';

            flags += `<div class="flex-row align-center" style="display: flex; align-items: center; justify-content: flex-start; gap: 2px;">
                {{scheduled_orders_check | icon}}
                <span><strong>${language.translate('LABEL.FLAG.SCHEDULE.ORDER')}</strong></span>
            </div>`;
            flags_obj['scheduled_orders_check'] = label_flag.scheduled_orders ? 'check' : 'remove';

            const sup_can_see = label_flag.supplier_visible ? language.translate('LABEL.FLAG.SUPPLIER.YES') : language.translate('LABEL.FLAG.SUPPLIER.NO')

            return {
                short_name: template.transform(`
                    <div class="flex-row align-center justify-center" style="width: 100%; padding-top: 0.5em;padding-bottom: .5em;">
                        <strong>${label_flag.short_name}</strong>
                    </div>
                `),
                name: template.transform(`
                    <div class="flex-row align-center">
                        <span>${label_flag.name}</span>
                    </div>
                `),

                flags: template.transform(`
                    <div class="flex-column flex-dynamic align-center" >
                        ${flags}
                    </div>
                `, flags_obj),

                supplier_visible: template.transform(`
                    <div class="flex-row align-center">
                        <span>${sup_can_see}</span>
                    </div>
                `),
            }
        },

        maxActions: window.innerWidth < 600 ? 1 : 3,
        rowActions: [{
                label: language.translate('EDIT'),
                icon: 'edit',
                onClick: async (label_flag) => {
                    modal.open(CreateEditLabelFlagsDialog, () => {
                        this.table.getData();
                    }, label_flag as any)
                },
                priority: 1,
                isVisible: () => {
                return User.currentUser?.user_type_id === Task.USER_TYPE.ADMIN ||
                    User.currentUser?.user_type_id === Task.USER_TYPE.SYSTEM_ADMIN;
                }
            },
            {
                label: language.translate('DELETE'),
                icon: "delete",
                color: "warn",
                onClick: (label_flag) => {
                    modal.open(ConfirmDialogComponent, async (succ) => {
                        if (succ) {
                            await TaskRoutes.label_flags.api_label_flags_delete(label_flag.label_id)
                            this.table.getData();
                        }
                    },
                    {
                        title: language.translate("LABEL.FLAG.DELETE.TITLE"),
                        message: language.translate("LABEL.FLAG.DELETE.CONTENT", label_flag.name),
                        icon: "delete",
                        iconColor: color.variable(color.Variable.warn)
                    });
                },
                priority: 2,
                isVisible: (label_flag: Task.LabelFlags) => {
                    const is_admin = User.currentUser?.user_type_id === Task.USER_TYPE.ADMIN ||
                    User.currentUser?.user_type_id === Task.USER_TYPE.SYSTEM_ADMIN;

                    const is_not_deleted = label_flag.status_id !== Task.LABEL_FLAG_STATUS.DELETED;

                    return is_admin && is_not_deleted;
                }
            }],

        filterComponent: GenericFilterComponent,
        filterInput: async () => {
            let filters: GenericFilterComponent["filterInput"] = [
                GenericFilter.text('LABEL.FLAG', "short_name+ +name"),
                GenericFilter.toggle("LABEL.FLAG.SUPPLIERS", "supplier_visible"),
                GenericFilter.dropdown("LABEL.FLAG.STATUS", "status_id", [
                    { label: 'LABEL.FLAG.STATUS.ACTIVE', value: Task.LABEL_FLAG_STATUS.ACTIVE },
                    { label: 'LABEL.FLAG.STATUS.DELETED', value: Task.LABEL_FLAG_STATUS.DELETED }
                ])
            ]

            if (await User.currentTypeIs(Task.USER_TYPE.SYSTEM_ADMIN)) {
                filters.push(GenericFilter.search("COMPANY", "company", this.search_companies, this.format_company_name));

            }

            return filters
        }
    }
}

interface DisplayLabelFlags {
    short_name: template.EscapedHTML,
    name: template.EscapedHTML,
    flags: template.EscapedHTML,
    supplier_visible: template.EscapedHTML,
}
